/**
 * Hotspots overlay — invisible-by-default DOM buttons pinned over
 * on-stage props for the parser-CYOA. Hovering shows a warm outline
 * around the prop; clicking fires the player's "examine" action.
 *
 * Each hotspot's screen rect tracks the prop's projected bounding box
 * every frame via requestAnimationFrame, so parallax / camera moves
 * keep the overlay glued to the prop without lag.
 */

import type { Stage } from '../stage.ts';
import type { VoiceKey } from '../script.ts';

export type HotspotItem =
  | {
      kind?: 'prop';
      /** Object id passed to the click callback. */
      id: string;
      /** Player-facing label shown as a tooltip on hover. */
      label: string;
      /** Stage prop key whose screen rect this hotspot tracks. */
      propKey: string;
    }
  | {
      kind: 'character';
      /** NPC id passed to the click callback (e.g. "__npc:frog"). */
      id: string;
      /** Player-facing label shown on hover. */
      label: string;
      /** VoiceKey of the on-stage character sprite to track. */
      spriteKey: VoiceKey;
    };

export class HotspotsUI {
  private root: HTMLElement;
  private overlays = new Map<string, HTMLButtonElement>();
  private items: HotspotItem[] = [];
  private rafId: number | null = null;

  constructor(
    private stage: Stage,
    private onClick: (objectId: string) => void,
  ) {
    let el = document.getElementById('hotspots') as HTMLElement | null;
    if (!el) {
      el = document.createElement('div');
      el.id = 'hotspots';
      el.className = 'hotspots';
      document.body.appendChild(el);
    }
    this.root = el;
  }

  show() { this.root.classList.remove('hidden'); }
  hide() { this.root.classList.add('hidden'); }

  /** Replace the active set of hotspots. Diffs against the current set
   *  so unchanged items aren't torn down. */
  set(items: HotspotItem[]) {
    const next = new Map(items.map(i => [i.id, i]));
    // Remove gone
    for (const [id, el] of this.overlays) {
      if (!next.has(id)) {
        el.remove();
        this.overlays.delete(id);
      }
    }
    // Add new
    for (const item of items) {
      if (this.overlays.has(item.id)) continue;
      const el = document.createElement('button');
      el.type = 'button';
      // `.character` adds a slightly different outline tone in CSS so
      // hovering an NPC reads as "talk to" not "examine". Optional.
      el.className = 'hotspot' + (item.kind === 'character' ? ' character' : '');
      el.dataset.label = item.label;
      const verb = item.kind === 'character' ? 'speak to' : 'examine';
      el.setAttribute('aria-label', `${verb} ${item.label}`);
      el.addEventListener('click', (ev) => {
        ev.preventDefault();
        this.onClick(item.id);
      });
      this.root.appendChild(el);
      this.overlays.set(item.id, el);
    }
    this.items = items;
    if (this.rafId === null) this.tick();
  }

  /** Stop the rAF loop and remove all overlays. Called when the play ends. */
  clear() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    for (const [, el] of this.overlays) el.remove();
    this.overlays.clear();
    this.items = [];
  }

  private tick = () => {
    for (const item of this.items) {
      const el = this.overlays.get(item.id);
      if (!el) continue;
      const rect = item.kind === 'character'
        ? this.stage.getCharacterScreenRect(item.spriteKey)
        : this.stage.getPropScreenRect(item.propKey);
      if (!rect) {
        // Prop / sprite not visible — hide overlay until it returns.
        if (el.style.display !== 'none') el.style.display = 'none';
        continue;
      }
      // A small inset on each side keeps the outline from overlapping
      // adjacent props when they're close (e.g. tightly packed grass).
      const PAD = 4;
      el.style.display = '';
      el.style.left = `${rect.x - PAD}px`;
      el.style.top = `${rect.y - PAD}px`;
      el.style.width = `${rect.w + PAD * 2}px`;
      el.style.height = `${rect.h + PAD * 2}px`;
    }
    this.rafId = requestAnimationFrame(this.tick);
  };
}
